import { z } from 'zod';
import {
  entityIdSchema,
  nonEmptyTextSchema,
  sha256Schema,
  utcDateTimeSchema,
} from '@kop/contracts';

import {
  atmosphericMeasurementRecordSchema,
  attestationRefSchema,
  evidenceRefV2Schema,
  principalSnapshotV2Schema,
  sourceSnapshotRefSchema,
} from './v2-domain.js';

const positiveVersionSchema = z.number().int().positive();

export const workPermitHazardGroupsV3 = [
  {
    code: '1',
    label: '추락',
    controls: [
      '작업발판·난간 설치',
      '안전대 체결',
      '개구부·단부 방호',
      '추락방호망 설치',
      '사다리 상태 확인',
      '악천후 시 작업중지',
    ],
  },
  {
    code: '2',
    label: '낙하물',
    controls: [
      '낙하물 방지망 설치',
      '방호선반 설치',
      '하부 출입통제',
      '자재 적치상태 확인',
      '상하 동시작업 금지',
      '유도자 배치',
    ],
  },
  {
    code: '3',
    label: '끼임·협착',
    controls: [
      '방호덮개 설치',
      '정비 시 LOTO',
      '기동장치 잠금',
      '말림 방지 보호구 확인',
      '2인 1조 작업',
      '방호장치 작동 확인',
    ],
  },
  {
    code: '4',
    label: '감전',
    controls: [
      '누전차단기 설치',
      '절연용 보호구 착용',
      '전선 피복 손상 없음',
      '충전부 방호',
      '검전·접지',
      '접지상태 확인',
    ],
  },
  {
    code: '5',
    label: '밀폐·질식',
    controls: [
      '작업 전 가스측정',
      '적정공기 유지',
      '강제환기 가동',
      '감시인 배치',
      '송기마스크 비치',
      '출입인원 확인',
    ],
  },
  {
    code: '6',
    label: '화기·화재',
    controls: [
      '화재감시자 배치',
      '소화기 비치',
      '인증 방화포 설치',
      '가연물 제거',
      '불티 비산방지',
      '잔불 확인',
    ],
  },
  {
    code: '7',
    label: '충돌·중장비',
    controls: [
      '유도자 배치',
      '출입통제',
      '후방경보 작동',
      '제한속도 준수',
      '운전자 자격 확인',
      '유도경로 확보',
    ],
  },
  {
    code: '8',
    label: '절단·베임',
    controls: [
      '안전가드 설치',
      '절단방지 장갑 착용',
      '보안면 착용',
      '공구·날 상태 확인',
      '돌출부 제거',
      '안전수칙 교육',
    ],
  },
  {
    code: '9',
    label: '보건',
    controls: [
      'MSDS 비치',
      '호흡보호구 착용',
      '국소배기·환기',
      '청력보호구 착용',
      '유해물질 표시',
      '세척·위생시설 확인',
    ],
  },
  {
    code: '10',
    label: '추가 위험',
    controls: ['현장 추가 위험과 통제조치', '작업 종료 후 정리정돈'],
  },
] as const;

export const workPermitHazardCodeV3Schema = z.enum([
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
]);
export const checklistResponseV3Schema = z.enum([
  'conforming',
  'correction-required',
  'not-applicable',
]);

export const requirementBasisV3Schema = z
  .object({
    classification: z.enum(['legal-minimum', 'company-control', 'technical-guide']),
    sourceRef: sourceSnapshotRefSchema,
    displayCitation: nonEmptyTextSchema.max(500),
  })
  .strict();

export const workStepV3Schema = z
  .object({
    stepId: entityIdSchema,
    sequence: z.number().int().positive(),
    description: nonEmptyTextSchema.max(1_000),
    hazardCodes: z.array(workPermitHazardCodeV3Schema).min(1),
    equipmentInstanceIds: z.array(entityIdSchema),
  })
  .strict();

export const correctionActionV3Schema = z
  .object({
    actionId: entityIdSchema,
    description: nonEmptyTextSchema.max(2_000),
    owner: nonEmptyTextSchema.max(200),
    dueAt: utcDateTimeSchema,
    evidenceRefs: z.array(evidenceRefV2Schema).min(1),
    verifiedBy: principalSnapshotV2Schema.optional(),
    verifiedAt: utcDateTimeSchema.optional(),
  })
  .strict();

/** Server-owned verification for an authoring-stage deficiency. */
export const checklistDeficiencyVerificationV3Schema = z
  .object({
    verificationId: entityIdSchema,
    description: nonEmptyTextSchema.max(2_000),
    evidenceRefs: z.array(evidenceRefV2Schema).min(1),
    verifiedBy: principalSnapshotV2Schema,
    verifiedAt: utcDateTimeSchema,
    serverReceiptId: entityIdSchema,
  })
  .strict();

export const permitChecklistItemV3Schema = z
  .object({
    checklistItemId: entityIdSchema,
    templateVersionRef: sourceSnapshotRefSchema,
    hazardCode: workPermitHazardCodeV3Schema,
    positiveStatement: nonEmptyTextSchema.max(500),
    requirementBasis: requirementBasisV3Schema,
    response: checklistResponseV3Schema,
    rationale: z.string().trim().max(1_000).optional(),
    applicabilityDecisionRef: sourceSnapshotRefSchema.optional(),
    /**
     * Authoring records the observed deficiency only. Ownership, due dates and
     * evidence belong to the execution/patrol correction workflow, which is
     * the only workflow allowed to resolve a deficient checklist item.
     */
    deficiencyNote: nonEmptyTextSchema.max(2_000).optional(),
    correction: correctionActionV3Schema.optional(),
    verification: checklistDeficiencyVerificationV3Schema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.response === 'correction-required' && !value.correction && !value.deficiencyNote) {
      context.addIssue({
        code: 'custom',
        path: ['deficiencyNote'],
        message: '보완 항목에는 결함 내용을 기록해야 합니다.',
      });
    }
    if (
      value.response !== 'correction-required' &&
      (value.correction || value.deficiencyNote || value.verification)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['deficiencyNote'],
        message: '보완이 아닌 항목에 결함 또는 조치를 연결할 수 없습니다.',
      });
    }
    if (value.response === 'not-applicable' && !value.applicabilityDecisionRef) {
      context.addIssue({
        code: 'custom',
        path: ['applicabilityDecisionRef'],
        message: '비적용 판정은 서버 정책의 버전 고정 근거가 필요합니다.',
      });
    }
  });

export const workPermitDetailsV3Schema = z
  .object({
    permitId: entityIdSchema,
    version: positiveVersionSchema,
    workTitle: nonEmptyTextSchema.max(300),
    // Display-only snapshot of the applicant business name. Scope and authority
    // remain bound by the canonical membership fields on the aggregate.
    applicantCompanyName: nonEmptyTextSchema.max(300).optional(),
    /** Authored expected crew size. Historical records may omit it. */
    plannedHeadcount: z.number().int().positive().max(100_000).optional(),
    /** Optional work-ledger note. Blank client values must be omitted. */
    remarks: nonEmptyTextSchema.max(2_000).optional(),
    workTypes: z.array(z.enum(['holiday', 'non-routine', 'emergency', 'other'])).min(1),
    workWindow: z
      .object({
        startsAt: utcDateTimeSchema,
        endsAt: utcDateTimeSchema,
        extensionEndsAt: utcDateTimeSchema.optional(),
      })
      .strict(),
    area: nonEmptyTextSchema.max(500),
    safetyContacts: z
      .array(
        z
          .object({
            roleCode: nonEmptyTextSchema.max(100),
            name: nonEmptyTextSchema.max(200),
            contact: nonEmptyTextSchema.max(100),
            identityId: entityIdSchema.optional(),
          })
          .strict(),
      )
      .min(1),
    emergencyContacts: z
      .array(
        z
          .object({ label: nonEmptyTextSchema.max(200), contact: nonEmptyTextSchema.max(100) })
          .strict(),
      )
      .min(1),
    evacuation: z
      .object({
        direction: nonEmptyTextSchema.max(500),
        assemblyPoint: nonEmptyTextSchema.max(500),
        mapEvidenceRefs: z.array(evidenceRefV2Schema),
        mapRequired: z.boolean().optional(),
        mapApplicabilityDecisionRef: sourceSnapshotRefSchema.optional(),
        mapPlan: z
          .object({
            backgroundManifestId: entityIdSchema.optional(),
            backgroundVersion: positiveVersionSchema.optional(),
            backgroundContentHash: sha256Schema.optional(),
            annotations: z
              .array(
                z
                  .object({
                    annotationId: entityIdSchema,
                    kind: z.enum(['work-location', 'route-point', 'assembly-point']),
                    xRatio: z.number().min(0).max(1),
                    yRatio: z.number().min(0).max(1),
                    sequence: z.number().int().positive(),
                  })
                  .strict(),
              )
              .max(200),
          })
          .strict()
          .superRefine((value, context) => {
            const backgroundFields = [
              value.backgroundManifestId,
              value.backgroundVersion,
              value.backgroundContentHash,
            ];
            if (backgroundFields.some((field) => field != null) && backgroundFields.some((field) => field == null))
              context.addIssue({
                code: 'custom',
                path: ['backgroundManifestId'],
                message: '배경을 사용하면 manifest, version, content hash를 함께 고정해야 합니다.',
              });
          })
          .optional(),
      })
      .strict(),
    gasMeasurementRequired: z.boolean(),
    gasApplicabilityDecisionRef: sourceSnapshotRefSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (Date.parse(value.workWindow.endsAt) <= Date.parse(value.workWindow.startsAt))
      context.addIssue({
        code: 'custom',
        path: ['workWindow', 'endsAt'],
        message: '종료시간은 시작시간 이후여야 합니다.',
      });
    if (
      value.workWindow.extensionEndsAt &&
      Date.parse(value.workWindow.extensionEndsAt) <= Date.parse(value.workWindow.endsAt)
    )
      context.addIssue({
        code: 'custom',
        path: ['workWindow', 'extensionEndsAt'],
        message: '연장 종료시간은 기존 종료시간 이후여야 합니다.',
      });
    if (value.gasMeasurementRequired && !value.gasApplicabilityDecisionRef)
      context.addIssue({
        code: 'custom',
        path: ['gasApplicabilityDecisionRef'],
        message: '가스측정 대상 작업은 게시된 적용 판단 근거가 필요합니다.',
      });
  });

export const physicalEquipmentV3Schema = z
  .object({
    equipmentInstanceId: entityIdSchema,
    equipmentType: z.enum([
      'mobile-crane',
      'excavator',
      'forklift',
      'aerial-platform',
      'gantry-crane',
      'overhead-crane',
      'other',
    ]),
    equipmentCategory: z
      .enum(['vehicle-construction', 'vehicle-handling', 'heavy-load', 'other'])
      .optional(),
    displayName: nonEmptyTextSchema.max(200),
    registrationNumber: z.string().trim().max(100).optional(),
    manufacturer: z.string().trim().max(200).optional(),
    model: z.string().trim().max(200).optional(),
    capacity: z.number().positive().optional(),
    capacityUnit: z.string().trim().max(40).optional(),
    sourceEquipmentVersionRef: sourceSnapshotRefSchema.optional(),
  })
  .strict();

export const formulaValueV3Schema = z
  .object({
    key: nonEmptyTextSchema.max(100),
    value: z.number().finite(),
    unit: nonEmptyTextSchema.max(40),
    evidenceRef: evidenceRefV2Schema.optional(),
  })
  .strict();

export const equipmentFormulaEvaluationV3Schema = z
  .object({
    formulaVersion: nonEmptyTextSchema.max(100),
    formulaPolicyRef: sourceSnapshotRefSchema.optional(),
    inputs: z.array(formulaValueV3Schema).min(1),
    coefficients: z.array(
      z
        .object({
          key: nonEmptyTextSchema.max(100),
          value: z.number().finite(),
          sourceRef: sourceSnapshotRefSchema,
        })
        .strict(),
    ),
    result: formulaValueV3Schema,
    evidenceRefs: z.array(evidenceRefV2Schema),
    evaluation: z.enum(['not-evaluated', 'ok', 'ng']),
    reviewedBy: principalSnapshotV2Schema.optional(),
    reviewedAt: utcDateTimeSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.formulaPolicyRef && value.evaluation !== 'not-evaluated') {
      context.addIssue({
        code: 'custom',
        path: ['evaluation'],
        message: '게시된 계산 규범이 없으면 자동 판정할 수 없습니다.',
      });
    }
    if (value.evaluation !== 'not-evaluated' && (!value.reviewedBy || !value.reviewedAt)) {
      context.addIssue({
        code: 'custom',
        path: ['reviewedBy'],
        message: '판정에는 검토자와 서버 시각이 필요합니다.',
      });
    }
  });

export const equipmentPlanV3Schema = z
  .object({
    planId: entityIdSchema,
    caseId: entityIdSchema,
    equipmentInstanceId: entityIdSchema,
    version: positiveVersionSchema,
    contentHash: sha256Schema,
    state: z.enum(['draft', 'review-required', 'ready', 'superseded', 'invalidated']),
    reviewReceipt: z
      .object({
        receiptId: entityIdSchema,
        planId: entityIdSchema,
        planVersion: positiveVersionSchema,
        reviewTargetHash: sha256Schema,
        decision: z.enum(['reviewed', 'returned']),
        calculationPolicyState: z.enum(['policy-bound', 'manual-review-required']),
        note: nonEmptyTextSchema.max(1_000),
        reviewedBy: principalSnapshotV2Schema,
        reviewedAt: utcDateTimeSchema,
      })
      .strict()
      .optional(),
    commonConditionsSourceHash: sha256Schema,
    overriddenFields: z.array(nonEmptyTextSchema.max(200)),
    applicablePlanKinds: z
      .array(
        z.enum([
          'vehicle-construction-machinery',
          'vehicle-handling-machinery',
          'heavy-load-handling',
          'mobile-crane',
          'general-equipment',
        ]),
      )
      .min(1)
      .optional(),
    equipment: physicalEquipmentV3Schema,
    workConditions: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    conditionalReview: z.discriminatedUnion('kind', [
      z
        .object({
          kind: z.literal('lifting'),
          loadDescription: nonEmptyTextSchema,
          boomLength: formulaValueV3Schema,
          workingRadius: formulaValueV3Schema,
          windSpeed: formulaValueV3Schema,
          riggingMethod: nonEmptyTextSchema,
          slingCount: z.number().int().positive(),
          slingAngle: formulaValueV3Schema,
          shackleCount: z.number().int().positive(),
          evaluations: z.array(equipmentFormulaEvaluationV3Schema).min(1),
        })
        .strict(),
      z
        .object({
          kind: z.literal('excavation'),
          terrain: nonEmptyTextSchema,
          ground: nonEmptyTextSchema,
          buriedUtilities: nonEmptyTextSchema,
          excavationSequence: nonEmptyTextSchema,
          evidenceRefs: z.array(evidenceRefV2Schema),
        })
        .strict(),
      z
        .object({
          kind: z.literal('general-equipment'),
          route: nonEmptyTextSchema,
          separationControls: nonEmptyTextSchema,
          evidenceRefs: z.array(evidenceRefV2Schema),
        })
        .strict(),
    ]),
    evidence: z
      .object({
        registration: z.array(evidenceRefV2Schema),
        inspection: z.array(evidenceRefV2Schema),
        insurance: z.array(evidenceRefV2Schema),
        operatorQualification: z.array(evidenceRefV2Schema),
        sitePhotos: z.array(evidenceRefV2Schema),
      })
      .strict(),
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.equipment.equipmentInstanceId !== value.equipmentInstanceId) {
      context.addIssue({
        code: 'custom',
        path: ['equipmentInstanceId'],
        message: '계획서는 물리적 장비 한 대에만 연결되어야 합니다.',
      });
    }
  });

export const tbmSnapshotV3Schema = z
  .object({
    tbmId: entityIdSchema,
    version: positiveVersionSchema,
    contentHash: sha256Schema,
    targetWorkHash: sha256Schema,
    targetPermitHash: sha256Schema,
    targetEquipmentPlanHashes: z.array(sha256Schema),
    checklist: z.array(nonEmptyTextSchema.max(500)).min(1),
    additionalHazards: z.array(
      z
        .object({
          description: nonEmptyTextSchema,
          owner: nonEmptyTextSchema,
          dueAt: utcDateTimeSchema.optional(),
          evidenceRefs: z.array(evidenceRefV2Schema),
        })
        .strict(),
    ),
    workerAttestations: z.array(attestationRefSchema).min(1),
    facilitatorAttestation: attestationRefSchema,
    evidenceRefs: z.array(evidenceRefV2Schema),
    lockedAt: utcDateTimeSchema,
  })
  .strict();

export const fieldAcknowledgementV3Schema = z
  .object({
    acknowledgementId: entityIdSchema,
    roleCode: z.enum(['contractor-site-representative', 'supervisor', 'operations-owner']),
    actor: principalSnapshotV2Schema,
    targetSnapshotHash: sha256Schema,
    acknowledgedAt: utcDateTimeSchema,
    receiptId: entityIdSchema,
  })
  .strict();

export const workPatrolAdditionalRiskV3Schema = z.object({
  itemId: z.string().regex(/^additional-[a-zA-Z0-9_-]{1,100}$/),
  statement: z.string().trim().min(1).max(500),
  result: z.enum(['normal', 'action-required', 'not-applicable']),
}).strict();
export type WorkPatrolAdditionalRiskV3 = z.infer<typeof workPatrolAdditionalRiskV3Schema>;
export const workPatrolCheckResultV3Schema = z.object({
  itemId: z.string().min(1).max(160),
  result: z.enum(['normal', 'action-required', 'not-applicable']),
}).strict();

export const workPatrolRecordV3Schema = z
  .object({
    patrolId: entityIdSchema,
    caseId: entityIdSchema,
    sequence: z.number().int().positive(),
    inspectedAt: utcDateTimeSchema,
    result: z.enum(['normal', 'action-required']),
    note: nonEmptyTextSchema.max(2_000),
    correction: z.string().trim().max(2_000).optional(),
    additionalRisks: z.array(workPatrolAdditionalRiskV3Schema).max(300).optional(),
    checkResults: z.array(workPatrolCheckResultV3Schema).max(300).optional(),
    inspector: principalSnapshotV2Schema,
    evidenceRefs: z.array(evidenceRefV2Schema),
    serverReceiptId: entityIdSchema,
    recordedAtServer: utcDateTimeSchema,
    overdue: z.boolean(),
  })
  .strict();

export const workPatrolCorrectionV3Schema = z
  .object({
    correctionId: entityIdSchema,
    patrolId: entityIdSchema,
    description: nonEmptyTextSchema.max(2_000),
    evidenceRefs: z.array(evidenceRefV2Schema).min(1),
    resolvedBy: principalSnapshotV2Schema,
    resolvedAt: utcDateTimeSchema,
    serverReceiptId: entityIdSchema,
  })
  .strict();

export const atmosphericMeasurementV3Schema = atmosphericMeasurementRecordSchema
  .safeExtend({
    confirmedBy: principalSnapshotV2Schema,
    serverReceiptId: entityIdSchema,
  })
  .strict()
  .superRefine((value, context) => {
    const requiredAnalytes = ['O2', 'combustible', 'CO2', 'CO', 'H2S'];
    const present = new Set(value.readings.map((reading) => reading.analyteCode));
    requiredAnalytes.forEach((analyte) => {
      if (!present.has(analyte))
        context.addIssue({
          code: 'custom',
          path: ['readings'],
          message: `${analyte} 측정값이 필요합니다.`,
        });
    });
  });

export const workCloseoutV3Schema = z
  .object({
    closeoutId: entityIdSchema,
    completionTime: utcDateTimeSchema,
    housekeeping: z.boolean(),
    facilityDamage: z.enum(['none', 'recorded']),
    allWorkersExited: z.boolean(),
    confirmedBy: principalSnapshotV2Schema,
    targetSnapshotHash: sha256Schema,
    evidenceRefs: z.array(evidenceRefV2Schema),
    serverReceiptId: entityIdSchema,
  })
  .strict();

export const workPrintBundleReceiptV3Schema = z
  .object({
    receiptId: entityIdSchema,
    workId: entityIdSchema,
    workVersion: positiveVersionSchema,
    permitVersion: positiveVersionSchema,
    equipmentPlanVersions: z.array(
      z
        .object({
          planId: entityIdSchema,
          equipmentInstanceId: entityIdSchema,
          version: positiveVersionSchema,
          contentHash: sha256Schema,
        })
        .strict(),
    ),
    orderedPages: z
      .array(
        z
          .object({
            artifactKind: z.enum(['work-permit', 'equipment-plan', 'annex']),
            artifactId: entityIdSchema,
            version: positiveVersionSchema,
            pageFrom: z.number().int().positive(),
            pageTo: z.number().int().positive(),
          })
          .strict(),
      )
      .min(1),
    pageCount: z.number().int().positive(),
    contentHash: sha256Schema,
    generatedAt: utcDateTimeSchema,
    generatedBy: principalSnapshotV2Schema,
    rendererVersion: nonEmptyTextSchema.max(100),
    invalidatesReceiptId: entityIdSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const calculated = value.orderedPages.reduce(
      (sum, page) => sum + page.pageTo - page.pageFrom + 1,
      0,
    );
    if (calculated !== value.pageCount)
      context.addIssue({
        code: 'custom',
        path: ['pageCount'],
        message: '페이지 수는 bundle manifest와 일치해야 합니다.',
      });
  });

export function buildWorkPrintBundlePagesV3(input: {
  permitId: string;
  permitVersion: number;
  equipmentPlans: readonly { planId: string; version: number; renderedPageCount: number }[];
}) {
  let nextPage = 1;
  const append = (
    artifactKind: 'work-permit' | 'equipment-plan',
    artifactId: string,
    version: number,
    renderedPageCount: number,
  ) => {
    if (!Number.isSafeInteger(renderedPageCount) || renderedPageCount < 1)
      throw new Error('RENDERED_PAGE_COUNT_REQUIRED');
    const pageFrom = nextPage;
    const pageTo = pageFrom + renderedPageCount - 1;
    nextPage = pageTo + 1;
    return { artifactKind, artifactId, version, pageFrom, pageTo } as const;
  };
  const orderedPages = [
    append('work-permit', input.permitId, input.permitVersion, 2),
    ...input.equipmentPlans.map((plan) =>
      append('equipment-plan', plan.planId, plan.version, plan.renderedPageCount),
    ),
  ];
  return { orderedPages, pageCount: nextPage - 1 };
}

export const workAggregateAllowedActionV3Schema = z.enum([
  'view',
  'edit',
  'submit',
  'invalidate',
  'delete-draft',
  'hard-delete',
  'review-equipment-plan',
  'record-tbm',
  'acknowledge-field',
  'publish',
  'record-patrol',
  'record-measurement',
  'confirm-closeout',
  'print',
  'start-correction',
  'verify-checklist-correction',
  'link-approval-request',
  'submit-approval',
  'issue-permit',
  'start-work',
  'resolve-patrol-correction',
]);

export const workApprovalLockV3Schema = z
  .object({
    requestId: entityIdSchema,
    receiptId: entityIdSchema,
    sourceRevisionId: entityIdSchema,
    sourceRevisionNumber: positiveVersionSchema,
    sourceContentHash: sha256Schema,
    approvedAt: utcDateTimeSchema,
    appliedAt: utcDateTimeSchema,
    immutable: z.literal(true),
  })
  .strict();

export const workExecutionStateV3Schema = z
  .object({
    issueReceiptId: entityIdSchema.optional(),
    issuedAt: utcDateTimeSchema.optional(),
    startReceiptId: entityIdSchema.optional(),
    startedAt: utcDateTimeSchema.optional(),
  })
  .strict();

export const workApprovalRouteV3Schema = z
  .object({
    routePolicyRef: sourceSnapshotRefSchema,
    submittedByIdentityId: entityIdSchema,
    stages: z
      .array(
        z
          .object({
            sequence: positiveVersionSchema,
            roleCode: z.enum(['safety-manager', 'supervisor', 'factory-manager']),
            eligibleIdentityIds: z.array(entityIdSchema).min(1).max(100).optional(),
            approvalMode: z.enum(['single', 'parallel-all']).optional(),
            requiredApprovals: positiveVersionSchema.optional(),
            status: z.enum(['pending', 'approved', 'returned']),
            approvalReceiptId: entityIdSchema.optional(),
          })
          .strict(),
      )
      .min(1)
      .max(3),
  })
  .strict()
  .superRefine((value, context) => {
    const initialSafety = value.stages[0];
    if (
      initialSafety?.sequence !== 1 ||
      initialSafety.roleCode !== 'safety-manager' ||
      (initialSafety.approvalMode != null &&
        (initialSafety.approvalMode !== 'single' ||
          initialSafety.requiredApprovals !== 1 ||
          !initialSafety.eligibleIdentityIds?.length ||
          initialSafety.eligibleIdentityIds.length > 20 ||
          new Set(initialSafety.eligibleIdentityIds).size !== initialSafety.eligibleIdentityIds.length))
    )
      context.addIssue({
        code: 'custom',
        path: ['stages', 0],
        message: '작업허가 결재는 공장의 안전관리자 1인 검토로 시작해야 합니다.',
      });
    if (value.stages.length === 1) return;
    const expected = ['safety-manager', 'supervisor', 'factory-manager'];
    if (
      value.stages.length !== 3 ||
      value.stages.some(
        (stage, index) => stage.sequence !== index + 1 || stage.roleCode !== expected[index],
      )
    )
      context.addIssue({
        code: 'custom',
        path: ['stages'],
        message: '작업허가 결재 경로는 안전담당자, 관리감독자, 공장장 순서로 고정됩니다.',
      });
    const supervisor = value.stages[1];
    if (
      supervisor?.approvalMode != null &&
      (supervisor.approvalMode !== 'parallel-all' ||
        supervisor.requiredApprovals !== 2 ||
        supervisor.eligibleIdentityIds?.length !== 2)
    )
      context.addIssue({
        code: 'custom',
        path: ['stages', 1],
        message: '관리감독자 단계는 서버가 고정한 2명의 병렬 전원 승인이어야 합니다.',
      });
  });

export const workMaterialRevisionV3Schema = z
  .object({
    revisionId: entityIdSchema,
    revision: positiveVersionSchema,
    previousVersion: positiveVersionSchema,
    priorSnapshotPath: nonEmptyTextSchema.max(500),
    reason: nonEmptyTextSchema.max(1_000),
    changedAt: utcDateTimeSchema,
    changedBy: principalSnapshotV2Schema,
    materialHash: sha256Schema,
    staleTbmId: entityIdSchema.optional(),
    staleAcknowledgementIds: z.array(entityIdSchema),
    staleEquipmentPlanIds: z.array(entityIdSchema),
    invalidatedPrintReceiptIds: z.array(entityIdSchema),
  })
  .strict();

export const workAggregateV3Schema = z
  .object({
    schemaVersion: z.literal(3),
    workId: entityIdSchema,
    version: positiveVersionSchema,
    scope: z.discriminatedUnion('kind', [
      z
        .object({
          kind: z.literal('site'),
          tenantId: z.literal('kop'),
          siteId: entityIdSchema,
        })
        .strict(),
      z
        .object({
          kind: z.literal('contractor'),
          tenantId: z.literal('kop'),
          siteId: entityIdSchema,
          contractorId: entityIdSchema,
        })
        .strict(),
    ]),
    source: z
      .object({
        channel: z.enum(['workspace', 'external-intake']),
        intakeReceiptId: entityIdSchema.optional(),
        applicantBindingId: entityIdSchema,
        contractorBindingId: entityIdSchema.optional(),
      })
      .strict(),
    lifecycle: z.enum([
      'draft',
      'returned',
      'approval-pending',
      'approved',
      'issued',
      'tbm-complete',
      'ready-for-tbm',
      'tbm-stale',
      'ready-to-publish',
      'active',
      'suspended',
      'completion-requested',
      'closed',
      'invalidated',
    ]),
    nextGate: z.enum([
      'permit-draft',
      'correction',
      'equipment-plans',
      'approval',
      'issue',
      'start',
      'tbm',
      'field-acknowledgement',
      'publish',
      'patrol',
      'gas-remeasurement',
      'closeout',
      'none',
    ]),
    applicantIdentityId: entityIdSchema,
    patrolAssigneeIdentityId: entityIdSchema,
    workDefinitionRef: sourceSnapshotRefSchema,
    workDefinitionHash: sha256Schema,
    permitDetails: workPermitDetailsV3Schema,
    permitVersion: positiveVersionSchema,
    permitContentHash: sha256Schema,
    workSteps: z.array(workStepV3Schema).min(1),
    checklist: z.array(permitChecklistItemV3Schema).min(1),
    equipment: z.array(physicalEquipmentV3Schema),
    equipmentPlans: z.array(equipmentPlanV3Schema),
    tbm: tbmSnapshotV3Schema.optional(),
    fieldAcknowledgements: z.array(fieldAcknowledgementV3Schema),
    patrols: z.array(workPatrolRecordV3Schema),
    patrolCorrections: z.array(workPatrolCorrectionV3Schema).default([]),
    atmosphericMeasurements: z.array(atmosphericMeasurementV3Schema),
    closeout: workCloseoutV3Schema.optional(),
    printBundles: z.array(workPrintBundleReceiptV3Schema),
    revisionHistory: z.array(workMaterialRevisionV3Schema).default([]),
    invalidatedPrintReceiptIds: z.array(entityIdSchema).default([]),
    approvalRoute: workApprovalRouteV3Schema.optional(),
    approvalRequestId: entityIdSchema.optional(),
    approvalLock: workApprovalLockV3Schema.optional(),
    execution: workExecutionStateV3Schema.optional(),
    allowedActions: z.array(workAggregateAllowedActionV3Schema),
    updatedAt: utcDateTimeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.applicantIdentityId !== value.patrolAssigneeIdentityId)
      context.addIssue({
        code: 'custom',
        path: ['patrolAssigneeIdentityId'],
        message: '신청자가 작업 중 점검 담당자로 고정되어야 합니다.',
      });
    if (value.scope.kind === 'contractor' && !value.source.contractorBindingId)
      context.addIssue({
        code: 'custom',
        path: ['source', 'contractorBindingId'],
        message: '협력업체 작업에는 서버가 검증한 업체 binding이 필요합니다.',
      });
    if (
      value.source.channel === 'external-intake' &&
      (!value.source.intakeReceiptId || value.scope.kind !== 'contractor')
    )
      context.addIssue({
        code: 'custom',
        path: ['source', 'intakeReceiptId'],
        message: '외부 신청은 접수 receipt와 협력업체 범위가 필요합니다.',
      });
    const equipmentIds = new Set(value.equipment.map((item) => item.equipmentInstanceId));
    const planIds = new Set<string>();
    value.equipmentPlans.forEach((plan, index) => {
      if (!equipmentIds.has(plan.equipmentInstanceId))
        context.addIssue({
          code: 'custom',
          path: ['equipmentPlans', index, 'equipmentInstanceId'],
          message: '선택하지 않은 장비의 계획서입니다.',
        });
      if (planIds.has(plan.equipmentInstanceId))
        context.addIssue({
          code: 'custom',
          path: ['equipmentPlans', index, 'equipmentInstanceId'],
          message: '물리적 장비 한 대에는 계획서 한 건만 허용됩니다.',
        });
      planIds.add(plan.equipmentInstanceId);
    });
  });

export type WorkAggregateV3 = z.infer<typeof workAggregateV3Schema>;
export type PhysicalEquipmentV3 = z.infer<typeof physicalEquipmentV3Schema>;
export type EquipmentPlanV3 = z.infer<typeof equipmentPlanV3Schema>;

export type WorkReadinessIssueV3 = { code: string; field: string; message: string };

/**
 * A checklist deficiency remains open until the execution-side correction
 * workflow records and verifies it. An authoring deficiency note documents the
 * problem but never acts as a correction receipt.
 */
export function workOpenChecklistCorrectionIssuesV3(
  work: Pick<WorkAggregateV3, 'checklist'>,
): WorkReadinessIssueV3[] {
  return work.checklist.flatMap((item) =>
    item.response === 'correction-required' &&
    (!item.verification && (!item.correction || !item.correction.verifiedAt))
      ? [
          {
            code: 'CORRECTION_OPEN',
            field: `checklist.${item.checklistItemId}`,
            message: '보완조치 확인이 필요합니다.',
          },
        ]
      : [],
  );
}

export function workPublishReadinessIssuesV3(work: WorkAggregateV3): WorkReadinessIssueV3[] {
  const issues: WorkReadinessIssueV3[] = [];
  const usedHazards = new Set(work.workSteps.flatMap((step) => step.hazardCodes));
  if (
    work.permitDetails.evacuation.mapRequired === true &&
    (!work.permitDetails.evacuation.mapApplicabilityDecisionRef ||
    !work.permitDetails.evacuation.mapPlan ||
    !work.permitDetails.evacuation.mapPlan.backgroundManifestId ||
    !work.permitDetails.evacuation.mapPlan.annotations.some(
      (annotation) => annotation.kind === 'work-location',
    ) ||
    !work.permitDetails.evacuation.mapPlan.annotations.some(
      (annotation) => annotation.kind === 'route-point',
    ) ||
    !work.permitDetails.evacuation.mapPlan.annotations.some(
      (annotation) => annotation.kind === 'assembly-point',
    ))
  )
    issues.push({
      code: 'EVACUATION_MAP_PLAN_MISSING',
      field: 'permitDetails.evacuation.mapPlan',
      message: '버전 고정 공장 배치도에 대피방향과 집결지를 표시해야 합니다.',
    });
  for (const code of usedHazards) {
    if (!work.checklist.some((item) => item.hazardCode === code))
      issues.push({
        code: 'CHECKLIST_MISSING',
        field: 'checklist',
        message: `${code}번 위험 확인이 없습니다.`,
      });
  }
  issues.push(...workOpenChecklistCorrectionIssuesV3(work));
  if (work.equipment.length !== work.equipmentPlans.length)
    issues.push({
      code: 'EQUIPMENT_PLAN_MISSING',
      field: 'equipmentPlans',
      message: '선택한 장비마다 작업계획서가 필요합니다.',
    });
  for (const plan of work.equipmentPlans) {
    if (
      plan.state !== 'ready' ||
      plan.reviewReceipt?.decision !== 'reviewed' ||
      plan.reviewReceipt.planId !== plan.planId ||
      plan.reviewReceipt.planVersion !== plan.version
    )
      issues.push({
        code: 'EQUIPMENT_PLAN_NOT_READY',
        field: `equipmentPlans.${plan.planId}`,
        message: `${plan.equipment.displayName} 작업계획서가 완료되지 않았습니다.`,
      });
    if (
      plan.conditionalReview.kind === 'lifting' &&
      plan.reviewReceipt?.calculationPolicyState === 'policy-bound'
    ) {
      for (const evaluation of plan.conditionalReview.evaluations) {
        if (evaluation.evaluation !== 'ok')
          issues.push({
            code: evaluation.evaluation === 'ng' ? 'FORMULA_NG' : 'FORMULA_REVIEW_REQUIRED',
            field: `equipmentPlans.${plan.planId}.formula`,
            message: `${plan.equipment.displayName} 안전성 검토가 완료되지 않았습니다.`,
          });
      }
    }
  }
  if (!work.tbm)
    issues.push({ code: 'TBM_MISSING', field: 'tbm', message: 'TBM과 서명이 필요합니다.' });
  else {
    const currentPlanHashes = [...work.equipmentPlans.map((plan) => plan.contentHash)].sort();
    const targetPlanHashes = [...work.tbm.targetEquipmentPlanHashes].sort();
    if (
      work.tbm.targetWorkHash !== work.workDefinitionHash ||
      work.tbm.targetPermitHash !== work.permitContentHash ||
      currentPlanHashes.join(':') !== targetPlanHashes.join(':')
    )
      issues.push({
        code: 'TBM_STALE',
        field: 'tbm',
        message: '작업 내용이 변경되어 TBM 재확인이 필요합니다.',
      });
  }
  if (work.fieldAcknowledgements.length < 2)
    issues.push({
      code: 'FIELD_ACKNOWLEDGEMENT_MISSING',
      field: 'fieldAcknowledgements',
      message: '협력업체와 현장 확인이 필요합니다.',
    });
  const latestMeasurement = work.atmosphericMeasurements.at(-1);
  if (latestMeasurement?.readings.some((reading) => reading.evaluation === 'deviation'))
    issues.push({
      code: 'GAS_DEVIATION',
      field: 'atmosphericMeasurements',
      message: '가스 기준 이탈로 작업 시작·재개가 차단되었습니다.',
    });
  return issues;
}

export function plansForSelectedEquipmentV3(input: {
  caseId: string;
  equipment: readonly PhysicalEquipmentV3[];
  commonConditionsSourceHash: string;
  now: string;
}): Array<
  Pick<
    EquipmentPlanV3,
    | 'planId'
    | 'caseId'
    | 'equipmentInstanceId'
    | 'equipment'
    | 'commonConditionsSourceHash'
    | 'version'
    | 'state'
    | 'overriddenFields'
    | 'workConditions'
    | 'updatedAt'
  >
> {
  const seen = new Set<string>();
  return input.equipment.map((equipment) => {
    if (seen.has(equipment.equipmentInstanceId)) throw new Error('DUPLICATE_PHYSICAL_EQUIPMENT');
    seen.add(equipment.equipmentInstanceId);
    return {
      planId: `plan-${input.caseId}-${equipment.equipmentInstanceId}`,
      caseId: input.caseId,
      equipmentInstanceId: equipment.equipmentInstanceId,
      equipment,
      commonConditionsSourceHash: input.commonConditionsSourceHash,
      version: 1,
      state: 'draft',
      overriddenFields: [],
      workConditions: {},
      updatedAt: input.now,
    };
  });
}
